import { Action } from 'redux';
import { ActionsObservable, ofType, StateObservable } from 'redux-observable';
import { concat, Observable, of } from 'rxjs';
import { catchError, mergeMap, switchMap, tap } from 'rxjs/operators';

import {
    ConfigPath,
    QuickSwitch,
    quickSwitchFilter,
    QuickSwitchItem,
    QuickSwitchType,
} from '../insight/config.insight.model';
import { legendRetrieveData, rootOverride } from '../insight/insight.action';
import {
    BaseChainGroup,
    pathSelect,
    viewSelect,
} from '../insight/insight.epic';
import { PerformanceDataViewType } from '../performance-data/metric.pd.model';
import { Dependencies } from '../store';

import * as actions from './human-insight.action';
import { storeStateMapper } from './human-insight.state';
import { SuperGroupExtra, TEAM_SUPER_GROUP_PATH } from './super-group.hi.model';

interface ChainGroup<T extends Action<actions.ActionType>>
    extends BaseChainGroup<T> {
    root: T;
    quickSwitches?: Array<QuickSwitch<SuperGroupExtra>>;
    quickSwitch?: QuickSwitch<SuperGroupExtra>;
    filteredItems?: Array<QuickSwitchItem<SuperGroupExtra>>;
    active?: QuickSwitchItem<SuperGroupExtra>;
}

function superGroupSelect<TAction extends Action<actions.ActionType>>(
    action: actions.SuperGroupSelectAction,
    deps: Dependencies,
    group: ChainGroup<TAction>,
): Observable<ChainGroup<TAction>> {
    group.active = action.superGroup;
    if (group.quickSwitches) {
        // TODO handle multiple quickswitches?
        group.quickSwitch = group.quickSwitches.find(
            (e) => e.quickSwitchType === QuickSwitchType.SUPER_GROUP,
        );
        group.filteredItems = quickSwitchFilter(
            group.quickSwitch,
            group.viewContext,
        ); // TODO revise. this'll be based on what ROOT selects
        if (
            group.filteredItems &&
            group.filteredItems.length !== 0 &&
            (group.active == null ||
                !group.filteredItems.some(
                    (e) => e.initialCriteria === group.active.initialCriteria,
                ))
        ) {
            group.active = group.filteredItems[0];
        }
    }
    return of(group);
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const superGroupSelectSuperGroup = (
    action: ActionsObservable<actions.SuperGroupSelectAction>,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(actions.ActionType.HUMAN_INSIGHT__SUPER_GROUP__SELECT),
        switchMap((act) => {
            const rootState = storeStateMapper(state.value);

            return of<ChainGroup<actions.SuperGroupSelectAction>>({
                root: act,
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                quickSwitches:
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    rootState.configs[rootState.activeConfig] &&
                    rootState.configs[rootState.activeConfig].quickSwitches,
                viewContext: rootState.viewContext,
                config: rootState.configs[rootState.activeConfig],
            }).pipe(
                mergeMap((group) =>
                    superGroupSelect<actions.SuperGroupSelectAction>(
                        act,
                        deps,
                        group,
                    ),
                ),
                mergeMap((group) =>
                    viewSelect(
                        {
                            view: group.config.views.find(
                                (e) => e.viewIdentifier === group.active.view,
                            ),
                        },
                        deps,
                        group,
                    ),
                ),
                mergeMap((group) => {
                    let path = group.config.paths.find(
                        (e) => e.pathIdentifier === group.active.path,
                    );
                    if (!path && group.view) {
                        path =
                            group.view.defaultPathIdentifier &&
                            group.config.paths.find(
                                (e) =>
                                    e.pathIdentifier ===
                                    group.view.defaultPathIdentifier,
                            );
                    }
                    if (!path) {
                        const sg = group.quickSwitches.find(
                            (e) =>
                                e.quickSwitchType ===
                                    QuickSwitchType.SUPER_GROUP &&
                                e.items.findIndex(
                                    (ee) =>
                                        !!ee.path &&
                                        group.config.paths.findIndex(
                                            (eee) =>
                                                eee.pathIdentifier === ee.path,
                                        ) !== -1,
                                ) !== -1,
                        );
                        path =
                            sg &&
                            group.config.paths.find(
                                (e) =>
                                    e.pathIdentifier ===
                                    sg.items.find((ee) => !!ee.path).path,
                            );
                    }
                    return pathSelect({ path }, deps, group).pipe(
                        tap((grp) => {
                            let pp: ConfigPath = grp.path;
                            if (grp.active.extra.teamSuperGroup) {
                                if (!pp) {
                                    pp = TEAM_SUPER_GROUP_PATH;
                                } else {
                                    pp = JSON.parse(JSON.stringify(pp));
                                    // assume (blindly) overall summary is in the root. ignore team summary
                                    pp.levels = (pp.levels || []).filter(
                                        (e) =>
                                            e.performanceDataViewType !==
                                                PerformanceDataViewType.TEAM_SUPER_GROUP &&
                                            e.performanceDataViewType ===
                                                PerformanceDataViewType.SUPER_GROUP,
                                    );
                                    pp.levels.unshift(
                                        TEAM_SUPER_GROUP_PATH.levels[0],
                                    );
                                }
                                grp.path = pp;
                            }
                            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                            grp.defaultPath =
                                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                                grp.path && grp.path.pathIdentifier;
                        }),
                    );
                }),
                mergeMap((group) =>
                    concat(
                        of(
                            rootOverride(
                                null,
                                group.view,
                                group.defaultPath,
                                group.path,
                            ),
                        ),
                        of(
                            actions.superGroupSelectResult(
                                group.quickSwitch,
                                group.filteredItems,
                                group.active,
                            ),
                        ),
                        of(legendRetrieveData()),
                    ),
                ),
                catchError(() => of(actions.superGroupSelectResult())),
            );
        }),
    );

export default [superGroupSelectSuperGroup];
